// Firebase Cloud Messaging client wrapper.
//
// Two-level responsibility:
//   1. Request browser Notification permission + register the FCM SW + exchange
//      for a device-scoped token.
//   2. Attach a foreground `onMessage` handler so the app can react to pushes
//      that land while the tab is focused (e.g., a toast + query invalidation).
//
// Token persistence + Firestore write live in `register-fcm.ts` (separate file
// so this module stays Firestore-free and easily mockable for tests).
//
// SCOPE NOTE (re-enabled May 2026): the VitePWA workbox SW owns scope `/`. The
// original FCM impl also registered at `/`, so the two SWs evicted each other
// on activation → controllerchange → the "nueva versión" reload loop (prod
// incident). Fix: register the FCM SW at its OWN sub-scope
// (`/firebase-cloud-messaging-push-scope`, FCM's own default) so both SWs
// coexist. No `injectManifest` merge needed.

import { getMessaging, getToken, onMessage, type Messaging, type MessagePayload } from 'firebase/messaging'
import { app } from '@/core/firebase/client'
import { firebaseConfig } from '@/core/firebase/config'
import { logger } from '@/core/logger'

let cachedMessaging: Messaging | null = null

/** Dedicated scope for the FCM SW so it never collides with the workbox SW at `/`. */
const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope'

/**
 * Lazy getter — throws if the browser lacks Notification / Service Worker
 * support, so callers can catch + no-op gracefully (Safari, Firefox private,
 * etc.).
 */
export function fcmMessaging(): Messaging {
  if (cachedMessaging) return cachedMessaging
  if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
    throw new Error('FCM not supported in this environment')
  }
  cachedMessaging = getMessaging(app)
  return cachedMessaging
}

export interface FcmRegisterOptions {
  /** VAPID public key from Firebase console → Project settings → Cloud Messaging → Web push certificates. */
  vapidKey: string
}

export interface FcmRegisterResult {
  token: string | null
  /** 'granted' / 'denied' / 'default' per the Notification API. */
  permission: NotificationPermission
}

/**
 * Web Push VAPID keys are a base64url-encoded uncompressed P-256 public key
 * (65 bytes raw → ~87 chars). Anything outside that shape will make
 * `PushManager.subscribe` throw `InvalidAccessError: applicationServerKey
 * is not valid` deep inside Firebase's getToken — detect the misconfig up
 * front and skip with an informative log. Sentry CLUBMANAGER-PWA-1D.
 */
function looksLikeVapidKey(key: string): boolean {
  return /^[A-Za-z0-9_-]{80,100}$/.test(key)
}

/** Wait for a scoped SW registration to reach `activated` (getToken needs an active SW). */
async function waitForActive(reg: ServiceWorkerRegistration): Promise<void> {
  if (reg.active) return
  const sw = reg.installing ?? reg.waiting
  if (!sw) return
  await new Promise<void>((resolve) => {
    const onChange = () => {
      if (sw.state === 'activated') {
        sw.removeEventListener('statechange', onChange)
        resolve()
      }
    }
    sw.addEventListener('statechange', onChange)
  })
}

/**
 * Request permission (if not decided), register the FCM SW at its dedicated
 * scope, and fetch the device token. Returns `{ token: null, permission }`
 * when the user declined or the browser isn't compatible.
 *
 * Enabled in dev AND prod — the dedicated scope (see SCOPE NOTE above) keeps
 * it from colliding with the workbox SW, so the prod reload-loop can't recur.
 */
export async function registerFcmDevice(opts: FcmRegisterOptions): Promise<FcmRegisterResult> {
  try {
    if (!looksLikeVapidKey(opts.vapidKey)) {
      logger.w(
        'FCM register skipped: VITE_FIREBASE_VAPID_KEY missing or malformed ' +
          '(expect ~87 base64url chars)',
      )
      return { token: null, permission: 'default' as NotificationPermission }
    }
    if (typeof Notification === 'undefined' || !('serviceWorker' in navigator)) {
      return { token: null, permission: 'default' as NotificationPermission }
    }
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return { token: null, permission }
    }
    // Register at the dedicated FCM scope (NOT `/`) so the workbox SW and this
    // SW coexist instead of evicting each other. Pass the env-selected Firebase
    // config (public client values) as query params so the static SW inits
    // against the RIGHT project in dev AND prod (it can't read import.meta.env).
    const swParams = new URLSearchParams({
      apiKey: firebaseConfig.apiKey ?? '',
      projectId: firebaseConfig.projectId ?? '',
      messagingSenderId: firebaseConfig.messagingSenderId ?? '',
      appId: firebaseConfig.appId ?? '',
    }).toString()
    const registration = await navigator.serviceWorker.register(
      `/firebase-messaging-sw.js?${swParams}`,
      { scope: FCM_SW_SCOPE },
    )
    await waitForActive(registration)
    const token = await getToken(fcmMessaging(), {
      vapidKey: opts.vapidKey,
      serviceWorkerRegistration: registration,
    })
    return { token: token ?? null, permission }
  } catch (err) {
    logger.w('FCM register failed', err)
    return { token: null, permission: 'default' as NotificationPermission }
  }
}

/**
 * Subscribe to foreground messages. Returns an unsubscribe function. The
 * callback receives the raw FCM payload — render a toast, invalidate queries,
 * or both. Safe to call multiple times (each call adds its own handler).
 */
export function onFcmForegroundMessage(cb: (payload: MessagePayload) => void): () => void {
  try {
    return onMessage(fcmMessaging(), cb)
  } catch (err) {
    logger.w('FCM onMessage failed', err)
    return () => {}
  }
}
